// frontend/src/components/common/Card.jsx
import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// ============================================================
// تعریف واریانت‌ها با cva
// ============================================================
const cardVariants = cva(
  'rounded-3xl bg-white/80 backdrop-blur-xl transition-all duration-300',
  {
    variants: {
      variant: {
        default: 'border border-white/90 shadow-soft',
        elevated: 'border border-white/90 shadow-card hover:shadow-card-hover',
        outline: 'border-2 border-slate-200 bg-transparent shadow-none hover:border-emerald-300',
        ghost: 'border-transparent bg-transparent shadow-none hover:bg-white/60',
        gradient: 'border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white shadow-soft',
        glass: 'border border-white/20 bg-white/10 backdrop-blur-xl shadow-xl text-white',
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
        xl: 'p-10',
      },
      hover: {
        true: 'hover:-translate-y-1.5 hover:shadow-card-hover',
        false: '',
      },
      clickable: {
        true: 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
      hover: true,
      clickable: false,
      fullWidth: false,
    },
  }
);

// ============================================================
// کامپوننت اصلی Card
// ============================================================
const Card = React.forwardRef(
  (
    {
      children,
      className,
      variant = 'default',
      padding = 'md',
      hover = true,
      clickable = false,
      fullWidth = false,
      as: Component = 'div',
      onClick,
      onKeyDown,
      role,
      tabIndex,
      header,
      footer,
      loading = false,
      loadingText = 'در حال بارگذاری...',
      ...props
    },
    ref
  ) => {
    const isClickable = clickable || typeof onClick === 'function';

    // ترکیب کلاس‌ها
    const cardClassName = cn(
      cardVariants({
        variant,
        padding,
        hover: hover && !loading,
        clickable: isClickable,
        fullWidth,
      }),
      className
    );

    // رندر محتوای اصلی
    const renderContent = () => {
      if (loading) {
        return (
          <div className="flex min-h-[120px] flex-col items-center justify-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
            {loadingText && (
              <span className="text-sm text-slate-400">{loadingText}</span>
            )}
          </div>
        );
      }

      return children;
    };

    // رندر با هدر و فوتر
    const renderWithHeaderFooter = () => {
      return (
        <>
          {header && (
            <div className="border-b border-slate-100 pb-4 mb-4">
              {typeof header === 'string' ? (
                <h3 className="text-lg font-black text-text-dark">{header}</h3>
              ) : (
                header
              )}
            </div>
          )}

          <div className="flex-1">{renderContent()}</div>

          {footer && (
            <div className="border-t border-slate-100 pt-4 mt-4">
              {typeof footer === 'string' ? (
                <div className="text-sm text-slate-500">{footer}</div>
              ) : (
                footer
              )}
            </div>
          )}
        </>
      );
    };

    return (
      <Component
        ref={ref}
        role={role || (isClickable ? 'button' : undefined)}
        tabIndex={tabIndex ?? (isClickable ? 0 : undefined)}
        onClick={isClickable ? onClick : undefined}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e);
          }
        }}
        className={cardClassName}
        {...props}
      >
        {header || footer ? renderWithHeaderFooter() : renderContent()}
      </Component>
    );
  }
);

Card.displayName = 'Card';

// ============================================================
// کامپوننت‌های فرعی Card
// ============================================================
export const CardHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('border-b border-slate-100 pb-4 mb-4', className)}
    {...props}
  >
    {children}
  </div>
));
CardHeader.displayName = 'CardHeader';

export const CardBody = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('flex-1', className)} {...props}>
    {children}
  </div>
));
CardBody.displayName = 'CardBody';

export const CardFooter = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('border-t border-slate-100 pt-4 mt-4', className)}
    {...props}
  >
    {children}
  </div>
));
CardFooter.displayName = 'CardFooter';

// ============================================================
// خروجی پیش‌فرض
// ============================================================
export default Card;